import {
  $headings,
  type EditorView,
  getHeadingFoldRange,
  type HeadingItem,
} from '@bangle.io/prosemirror-plugins';
import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import type { PmEditorService } from '../pm-editor-service';
import { useEditorCoreServices } from '../use-editor-core-services';
import { TocRail } from './toc-rail';

function expandCollapsedAncestors(
  view: EditorView,
  targetPos: number,
  ext: PmEditorService['extensions'],
) {
  const collapsed = ext.collapsibleHeading.query.listCollapsedHeadings(
    view.state,
  );
  for (const { pos: headingPos } of collapsed) {
    const range = getHeadingFoldRange(view.state.doc, headingPos);
    if (!range) {
      continue;
    }
    if (targetPos >= range.from && targetPos < range.to) {
      ext.collapsibleHeading.command.toggleHeadingCollapseAtPos(headingPos)(
        view.state,
        view.dispatch,
        view,
      );
    }
  }
}

/**
 * `getEditor` is not reactive on its own. Re-check whenever `$headings`
 * changes (plugin publish) and poll briefly while the async mount is pending.
 */
function useEditorView(editorName: string) {
  const { editorEngine } = useEditorCoreServices();
  const headingsByView = useAtomValue($headings);
  const [editorView, setEditorView] = useState(() =>
    editorEngine.getEditor(editorName),
  );

  useEffect(() => {
    // `$headings` publishes are the signal that the view finished mounting
    // or the doc outline changed; `getEditor` itself is not reactive.
    void headingsByView;
    setEditorView(editorEngine.getEditor(editorName));
  }, [editorEngine, editorName, headingsByView]);

  useEffect(() => {
    if (editorView) {
      return;
    }
    const id = window.setInterval(() => {
      const next = editorEngine.getEditor(editorName);
      if (next) {
        setEditorView(next);
        window.clearInterval(id);
      }
    }, 50);
    return () => window.clearInterval(id);
  }, [editorEngine, editorName, editorView]);

  return editorView;
}

/**
 * Editor-bound TOC rail: resolves the live view through the editor engine,
 * tracks the outline the `heading-watch` plugin publishes, and expands any
 * collapsed ancestors before a jump. The rail itself is `TocRail`, which the
 * standalone reader page mounts without any of this.
 */
export function HeadingTocRail({ editorName }: { editorName: string }) {
  const { editorEngine } = useEditorCoreServices();
  const headingsByView = useAtomValue($headings);
  const editorView = useEditorView(editorName);
  const headings = editorView ? headingsByView.get(editorView) : undefined;

  const handleJump = useCallback(
    (item: HeadingItem, view: EditorView) => {
      expandCollapsedAncestors(view, item.pos, editorEngine.extensions);
    },
    [editorEngine],
  );

  return (
    <TocRail editorView={editorView} headings={headings} onJump={handleJump} />
  );
}
